// i18n fragment - populated by the Phase 6 sweep. See ../strings.ts.
// Covers web-ui StudioView.vue (studio.* - extends the base studio.* keys
// already defined in strings.ts) and MeView.vue (me.*). Keys already in the
// base table (studio.pending.* / studio.phase.* / studio.tools.* / the base
// studio.share.* set / common.*) are referenced from there, NOT redefined here.
import type { LocaleStrings } from './base';

export const studioMeStrings: LocaleStrings = {
  en: {
    // ── web-ui StudioView.vue (studio.*, extending base studio.*) ──
    'studio.title':                     'Studio',
    'studio.back_to_list':              'Back to conversations',
    'studio.untitled':                  'Untitled',
    'studio.agent_fallback':            'the agent',
    'studio.usage_tip':                 '{new} new + {cache} cached tokens · {turns} turns',
    'studio.cols_empty':                'Every column is hidden. Use the toggles in the header to bring one back.',
    'studio.reconnecting':              'Reconnecting…',
    'studio.stopped':                   'Stopped',
    'studio.resize_tip':                'Drag to resize',
    'studio.expand_tip':                'Expand',
    'studio.expand_aria':               'Expand {name}',

    // Conversation title (rename inline).
    'studio.title.placeholder':         'Name this conversation',
    'studio.title.rename_tip':          'Rename - conv {id}',
    'studio.title.renamed':             'Conversation renamed',
    'studio.title.reverted':            'Title reverted to auto',
    'studio.title.rename_error':        'Could not rename: {error}',

    // Senders.
    'studio.sender.you':                'You',
    'studio.sender.user':               'User',
    'studio.sender.assistant':          'Assistant',

    // Date dividers between message groups (transcript day breaks).
    'studio.day.today':                 'Today',
    'studio.day.yesterday':             'Yesterday',

    // Pane toggles (chat / files / editor / preview).
    'studio.pane.chat':                 'Chat',
    'studio.pane.document':             'Document',
    'studio.pane.files':                'Files',
    'studio.pane.editor':               'Editor',
    'studio.pane.preview':              'Preview',
    'studio.pane.toggle_tip':           'Toggle {label}',
    'studio.pane.toggle_aria':          'Toggle {label} column',
    'studio.pane.switch_aria':          'Show {label}',
    'studio.mobile.hint':               'Studio works best on a larger screen. Switch panes with the tabs above.',
    'studio.mobile.hint_dismiss':       'Dismiss',

    // Fresh-thread empty state: the stage panes are content-gated (they only
    // exist once the agent makes something), so a brand-new conversation says
    // what WILL appear and where. Rows are thread-kind aware: fast threads
    // (no VM) only get the document row.
    'studio.empty.heading':             'What the agent makes opens beside the chat.',
    'studio.empty.app':                 'An app you ask for shows up as a live preview.',
    'studio.empty.doc':                 'A document opens in the editor.',
    'studio.empty.files':               'Working files land in the Files pane.',

    // Document column: the tab strip + the "documents in this conversation"
    // overflow menu + the chat doc-card.
    'studio.doc.close_tab':             'Close {name}',
    'studio.doc.reopen':                'Reopen {name}',
    'studio.doc.changes':               'Suggested changes ({count})',
    'studio.doc.download_tip':          'Download .docx',
    'studio.doc.download_pdf_tip':      'Download as PDF',
    'studio.doc.load_failed':           'Could not load the conversation documents.',
    'studio.doc.card_opened':           'Document opened',
    'studio.doc.card_open_tip':         'Open {name}',

    // Composer.
    'studio.composer.ask_name':         'Ask {name}…',
    'studio.composer.message_agent':    'Message the agent…',
    'studio.composer.attach_tip':       'Attach files - up to {max}, {cap} MB each',
    'studio.composer.send':             'Send',
    'studio.composer.stop':             'Stop',
    'studio.composer.stop_tip':         'Stop the agent',
    'studio.composer.remove_ref':       'Remove reference',
    'studio.composer.remove_ref_aria':  'Remove reference {name}',
    'studio.composer.remove_file':      'Remove {name}',

    // Convert a fast thread to a full agent thread.
    'studio.convert.title':             'Convert to an agent thread?',
    'studio.convert.body':              'This thread will get its own sandbox so the agent can run tasks, edit files, and build apps. The conversation so far carries over.',
    'studio.convert.confirm':           'Convert',
    'studio.convert.done':              'Converted to an agent thread',
    'studio.convert.tip':               'Give this thread a sandbox so the agent can run tasks',
    'studio.convert.button':            'Convert to agent',

    // Delete app (surface).
    'studio.del_app.title':             'Delete this app?',
    'studio.del_app.body':              "This destroys the app's sandbox and its persistent disk. The conversation stays. This cannot be undone.",
    'studio.del_app.confirm':           'Delete app',
    'studio.del_app.tip':               'Delete the app {slug}',
    'studio.del_app.button':            'Delete app',

    // Delete the whole conversation.
    'studio.del_conv.title':            'Delete conversation?',
    'studio.del_conv.body':             'This permanently deletes the conversation and its messages. This cannot be undone.',
    'studio.del_conv.body_app':         'This permanently deletes the conversation and its messages, and destroys its app "{slug}". This cannot be undone.',
    'studio.del_conv.confirm_word':     'delete',
    'studio.del_conv.confirm_label':    'Delete conversation',
    'studio.del_conv.also_files_one':   "Also delete this conversation's {count} delivered file (otherwise kept, still downloadable)",
    'studio.del_conv.also_files_other': "Also delete this conversation's {count} delivered files (otherwise kept, still downloadable)",
    'studio.del_conv.done':             'Conversation deleted',
    'studio.del_conv.tip':              'Delete conversation',
    'studio.del_conv.aria':             'Delete conversation',

    // Share modal (only the key not already in base).
    'studio.share.mode_aria':           'Share mode',

    // Drop-to-attach overlay (chat column).
    'studio.drop.title':                'Drop to attach',
    'studio.drop.hint':                 'Up to {max} files, {cap} MB each',

    // Discard-unsaved-edits confirm.
    'studio.discard.title':             'Discard unsaved changes?',
    'studio.discard.body':              'You have unsaved edits in the open file. Leaving will discard them.',
    'studio.discard.confirm':           'Discard',

    // Files pane (the live /work tree + conversation files).
    'studio.files.title':               'Files',
    'studio.files.upload_tip':          'Upload to this folder',
    'studio.files.new_file_tip':        'New file',
    'studio.files.new_folder_tip':      'New folder',
    'studio.files.refresh_tip':         'Refresh',
    'studio.files.zip_tip':             'Download the workspace as a zip',
    'studio.files.zip_aria':            'Download workspace',
    'studio.files.offline_paused':      'The app is paused. Resume it to browse and edit its files.',
    'studio.files.offline_asleep':      'The workspace is asleep. It wakes on the next message.',
    'studio.files.resume':              'Resume',
    'studio.files.check_again':         'Check again',
    'studio.files.empty_folder':        'Empty folder.',
    'studio.files.dl_all_tip':          'Download all delivered files as a zip',
    'studio.files.dl_all_aria':         'Download all files',
    'studio.files.published':           'Published',
    'studio.files.published_tip':       'Delivered by the agent',
    'studio.files.download':            'Download',
    'studio.files.download_aria':       'Download {name}',
    'studio.files.drop_title':          'Drop to upload',
    'studio.files.drop_hint':           'Files land in {path}',
    'studio.send_to_slack.action':      'Send to Slack',
    'studio.send_to_slack.title':       'Send to Slack',
    'studio.send_to_slack.subtitle':    'Post {name} to a Slack channel.',
    'studio.send_to_slack.channel_label': 'Channel',
    'studio.send_to_slack.channel_ph':  'Pick a channel',
    'studio.send_to_slack.loading':     'Loading channels...',
    'studio.send_to_slack.not_connected': 'This workspace has no Slack connection.',
    'studio.send_to_slack.no_channels': 'HQ is not in any channel yet. Add the HQ app to a channel first.',
    'studio.send_to_slack.send':        'Send',
    'studio.send_to_slack.sending':     'Sending...',
    'studio.send_to_slack.cancel':      'Cancel',
    'studio.send_to_slack.sent':        'Sent to {channel}',
    'studio.send_to_slack.failed':      'Could not send: {error}',

    // File-system prompts + validation.
    'studio.fs.name_required_file':     'Enter a file name.',
    'studio.fs.name_required_folder':   'Enter a folder name.',
    'studio.fs.name_no_slashes':        'Name cannot contain slashes.',
    'studio.fs.name_real':              'Pick a real name.',
    'studio.fs.new_file_title':         'New file',
    'studio.fs.new_folder_title':       'New folder',
    'studio.fs.name_label':             'Name',
    'studio.fs.create':                 'Create',
    'studio.fs.created_in':             'Created in {path}',
    'studio.fs.delete_title':           'Delete {name}?',
    'studio.fs.delete_dir_body':        'This deletes the folder and everything in it. This cannot be undone.',
    'studio.fs.delete_file_body':       'This deletes the file. This cannot be undone.',
    'studio.fs.drop_asleep':            'The workspace is asleep, so files can\'t be saved yet. Send a message to wake it, then try again.',
    'studio.fs.zip_error':              'Could not build the workspace zip.',
    'studio.fs.download_failed':        'Download failed.',

    // Editor toolbar + empty state.
    'studio.editor.unsaved':            'Unsaved changes',
    'studio.editor.preview':            'Preview',
    'studio.editor.view_source':        'View source',
    'studio.editor.referenced':         'Referenced - click to remove',
    'studio.editor.reference':          'Point the agent at this file',
    'studio.editor.reference_aria':     'Reference {name} in the composer',
    'studio.editor.close':              'Close',
    'studio.editor.edit_copy':          'Edit a copy',
    'studio.editor.empty':              'Open a file to view or edit it here.',

    // Preview pane (live iframe + state cards).
    'studio.preview.live':              'Live preview',
    'studio.preview.meta_tip':          '{slug} - {mode}, port {port}',
    'studio.preview.frame_tip':         'Live preview of {slug}',
    'studio.preview.inspect_start':     'Pick an element to point the agent at',
    'studio.preview.inspect_cancel':    'Cancel element picker',
    'studio.preview.open_tab':          'Open in a new tab',
    'studio.preview.empty':             'No preview yet. Ask the agent to build an app and it appears here.',
    'studio.preview.title_resuming':    'Resuming the app',
    'studio.preview.title_paused':      'The app is paused',
    'studio.preview.title_booting':     'Starting the app',
    'studio.preview.title_archived':    'The app is archived',
    'studio.preview.title_failed':      'The app failed to start',
    'studio.preview.title_destroyed':   'The app was deleted',
    'studio.preview.title_other':       'App state: {state}',
    'studio.preview.body_resuming':     'Restoring the sandbox from its last snapshot. This takes a few seconds.',
    'studio.preview.body_paused':       'It was put to sleep to save resources. Resume it to pick up where it left off.',
    'studio.preview.body_booting':      'Bringing the sandbox up. This takes a few seconds.',
    'studio.preview.body_archived':     'Its sandbox was archived. Ask the agent to rebuild it to bring it back.',
    'studio.preview.body_failed':       'Something went wrong starting it. Ask the agent to try again.',
    'studio.preview.body_destroyed':    'Its sandbox and disk were removed. Ask the agent to rebuild it.',
    'studio.preview.resuming':          'Resuming…',
    'studio.preview.resume_app':        'Resume app',

    // Preview runtime errors (postMessage from the iframe).
    'studio.preview_err.runtime':       'Runtime error',
    'studio.preview_err.rejection':     'Unhandled rejection: {reason}',
    'studio.preview_err.build':         'Build error ({source})',
    'studio.preview_err.dismiss_all':   'Dismiss all {count}',
    'studio.preview_err.fix':           'Try to fix',
    'studio.preview_err.dismiss':       'Dismiss',

    // Read-only banners + CTAs.
    'studio.readonly.schedule':         'This is a record of a scheduled run with {name}, not a live chat.',
    'studio.readonly.opening':          'Opening…',
    'studio.readonly.start_with':       'Start a fresh chat with {name}',
    'studio.readonly.channel':          'This conversation lives in the channel - reply there to respond.',
    'studio.readonly.admin':            'Viewing as admin - read-only.',

    // Admin-view banner + non-participant offer.
    'studio.admin.banner_label':        'Admin view.',
    'studio.admin.banner_text':         "You're viewing a conversation you don't take part in. Every load is audited.",
    'studio.admin.leave_tip':           'Leave admin view',
    'studio.admin.exit':                'Exit',
    'studio.admin.offer_title':         "You don't take part in this conversation",
    'studio.admin.offer_detail':        'As a workspace admin you can open it in admin view. Doing so is audited.',
    'studio.admin.offer_btn':           'Open in admin view',

    // Errors (banners, toasts, history loads, sends).
    'studio.err.unknown':               'unknown error',
    'studio.err.no_response':           'No response from the agent. Try sending again.',
    'studio.err.file_too_large':        'That file is too large to open here.',
    'studio.err.resume':                'Could not resume the app (HTTP {status}): {detail}',
    'studio.err.share_create':          'Could not create a share link (HTTP {status}): {detail}',
    'studio.err.share_public':          'Could not make the app public (HTTP {status}): {detail}',
    'studio.err.share_private':         'Could not switch back to invite-only (HTTP {status}): {detail}',
    'studio.err.history_load':          'Could not load the history (HTTP {status}).',
    'studio.err.history_load_failed':   'Could not load the history: {error}',
    'studio.err.send':                  'Could not send the message (HTTP {status}).',
    'studio.err.send_failed':           'Could not send the message: {error}',
    'studio.err.stop':                  'Could not stop the agent: {error}',

    // Turn-failure one-liners (raw rust error mapped to friendly copy).
    'studio.turn_err.unreachable':      'The agent could not finish - its workspace was unreachable. Send again to retry.',
    'studio.turn_err.timeout':          'The agent could not finish in time. Send again to retry.',
    'studio.turn_err.rate_limit':       'The agent is rate-limited right now. Wait a moment and send again.',
    'studio.turn_err.generic':          "The agent could not finish that turn. Send again to retry.",

    // Upload caps + failures.
    'studio.upload.too_big':            '{name} is {size} MB - over the {cap} MB limit.',
    'studio.upload.too_many':           'You can attach up to {max} files at a time.',
    'studio.upload.failed':             'Could not upload {name}: {error}',

    // ── web-ui MeView.vue (me.*) ──
    'me.header.title':                  'You',
    'me.header.subtitle':               'What your agents know about you, and how they remember it.',

    // Clock + timezone preference controls.
    'me.clock.auto':                    'Auto',
    'me.clock.12h':                     '12-hour',
    'me.clock.24h':                     '24-hour',
    'me.tz.device':                     'This device',
    'me.tz.profile':                    'My profile',

    // Identity ("You") block.
    'me.identity.heading':              'You',
    'me.avatar.change':                 'Change avatar',
    'me.avatar.uploading':              'Uploading…',
    'me.avatar.hint':                   'PNG, JPG, SVG, WebP or GIF, up to 2 MB.',
    'me.avatar.synced':                 'Your avatar is synced from your connected Slack/Teams profile.',
    'me.avatar.updated':                'Avatar updated.',
    'me.avatar.err_type':               'Unsupported image type. Use PNG, JPG, SVG, WebP or GIF.',
    'me.avatar.err_size':               'Image is over 2 MB. Resize it and try again.',
    'me.avatar.err_upload':             'Couldn\'t upload that image. Try again.',
    'me.identity.name':                 'Name',
    'me.identity.name_aria':            'Your name',
    'me.identity.name_ph':              'Your name',
    'me.identity.email':                'Email',
    'me.identity.title':                'Title',
    'me.identity.connected_as':         'Connected as',
    'me.identity.locale':               'Locale',
    'me.identity.locale_ph':            'Choose a locale',
    'me.identity.timezone':             'Timezone',
    'me.identity.tz_ph':                'Choose your timezone',
    'me.identity.tz_aria':              'Your timezone',
    'me.identity.tz_segment_aria':      'Timezone for displayed times',
    'me.identity.tz_showing':           'Showing times in {tz}.',
    'me.identity.tz_device_switch':     "This device's zone. Switch to show times in {tz}.",
    'me.identity.tz_none':              "No timezone on your workspace profile, so times use this device's zone.",
    'me.identity.role':                 'Role',
    'me.identity.role_admin':           'Workspace admin',
    'me.identity.role_member':          'Member',
    'me.identity.schedules':            'Schedules',
    'me.identity.schedules_owned':      '{count} owned',
    'me.identity.clock':                'Clock',
    'me.identity.clock_aria':           'Clock format',
    'me.identity.clock_hint':           'Times across the app, e.g. {sample}. Auto follows your browser.',

    // Persona block.
    'me.persona.heading':               'Persona',
    'me.persona.distilled':             'Distilled {when} from {count} messages',
    'me.persona.topics':                'Recurring topics',
    'me.persona.themes':                'Recurring themes',
    'me.persona.style':                 'Style',
    'me.persona.style_tone':            'Tone',
    'me.persona.style_length':          'Length',
    'me.persona.style_emoji':           'Emoji',
    'me.persona.style_punctuation':     'Punctuation',
    'me.persona.languages':             'Languages',
    'me.persona.lang_primary':          'primary',
    'me.persona.active_hours':          'Active hours',
    'me.persona.top_channels':          'Top channels',
    'me.persona.top_collaborators':     'Top collaborators',
    'me.persona.empty_before':          "No persona yet. HQ builds a profile from your past messages and refreshes it weekly. If you've only been on the workspace a short time, or if your tenant hasn't opted in to per-user distillation (consent:",
    'me.persona.empty_after':           '), this stays empty.',

    // Memory facts block.
    'me.facts.heading':                 'What agents have written',
    'me.facts.count_one':               '{count} fact',
    'me.facts.count_other':             '{count} facts',
    'me.facts.filter_all':              'All',
    'me.facts.show_more':               'Show more',
    'me.facts.empty':                   'No facts on file. Agents write facts here when they learn something stable about you - preferences, recurring context, how you like things. They never include conversation contents, only distilled, person-specific statements.',
    'me.facts.empty_filtered':          'No facts of type {type}. Agents write facts here when they learn something stable about you - preferences, recurring context, how you like things. They never include conversation contents, only distilled, person-specific statements.',

    // A single fact row (inline edit + actions).
    'me.fact.will_replace':             'will replace, not delete',
    'me.fact.save_aria':                'Save correction',
    'me.fact.cancel_aria':              'Cancel edit',
    'me.fact.edit_aria':                'Edit this fact',
    'me.fact.forget_aria':              'Forget this fact',
    'me.fact.forget':                   'Forget',
    'me.fact.length_warn':              'A fact must be between 1 and 4096 characters.',

    // Per-agent memory freeze block.
    'me.freeze.heading':                'Agents that can read your memory',
    'me.freeze.lede':                   "Each agent can normally read every fact other agents have written about you, so they're not asking the same questions twice. Freeze an agent to cut it off from your memory - it stops seeing your facts on its next conversation. New facts written by other agents still land in your memory; you just decide who gets to read them.",
    'me.freeze.frozen_at':              'Frozen {when}',
    'me.freeze.can_read':               'Can read your memory',
    'me.freeze.freeze':                 'Freeze',
    'me.freeze.unfreeze':               'Unfreeze',
    'me.freeze.empty':                  'No agents enabled in this workspace yet.',

    // Right-to-erasure block.
    'me.erase.heading':                 'Erase my data',
    'me.erase.body_before':             "GDPR Article 17 - request permanent deletion of your personal data in this workspace. Scrubs your DM message bodies, every fact agents have recorded about you, the corpus rows you authored, your persona profile, and any private OAuth credentials you installed. Your workspace account stays as a tombstone so other people's history stays intact.",
    'me.erase.body_warn':               "This can't be undone.",
    'me.erase.button':                  'Erase my data',
    'me.erase.erasing':                 'Erasing…',
    'me.erase.confirm_title':           'Erase your data?',
    'me.erase.confirm_body':            "This permanently deletes your personal data in this workspace - messages, facts, corpus rows, persona, and private credentials. This can't be undone.",
    'me.erase.confirm_word':            'erase',
    'me.erase.confirm_label':           'Erase my data',
    'me.erase.done':                    'Erased {msgs} messages, {facts} facts, {rows} corpus rows, and {creds} credentials.',

    // Save / action error toasts.
    'me.err.save_clock':                'Could not save your clock preference.',
    'me.err.save_timezone':             'Could not save your timezone.',
    'me.err.save_name':                 'Could not save your name.',
    'me.err.save_locale':               'Could not save your locale.',
    'me.err.erase_failed':              'Erasure failed. Nothing was deleted.',
    'me.err.save_failed':               'Could not save your correction.',
    'me.err.forget_failed':             'Could not forget that fact.',
    'me.err.action_failed':             'Something went wrong.',

    // Save / action success toasts.
    'me.toast.name_updated':            'Name updated',
    'me.toast.timezone_updated':        'Timezone updated',
    'me.toast.locale_updated':          'Language updated',

    // Forget-fact confirm.
    'me.forget.confirm_title':          'Forget this fact?',
    'me.forget.confirm_body':           'HQ will stop using "{fact}". This cannot be undone.',
    'me.forget.confirm_label':          'Forget',
  },
  sv: {
    // ── web-ui StudioView.vue (studio.*, utökar bas-studio.*) ──
    'studio.title':                     'Studio',
    'studio.back_to_list':              'Tillbaka till konversationer',
    'studio.untitled':                  'Namnlös',
    'studio.agent_fallback':            'agenten',
    'studio.usage_tip':                 '{new} nya + {cache} cachade tokens · {turns} turer',
    'studio.cols_empty':                'Alla kolumner är dolda. Använd knapparna i sidhuvudet för att ta fram en igen.',
    'studio.reconnecting':              'Återansluter…',
    'studio.stopped':                   'Stoppad',
    'studio.resize_tip':                'Dra för att ändra storlek',
    'studio.expand_tip':                'Förstora',
    'studio.expand_aria':               'Förstora {name}',

    // Konversationstitel (byt namn inline).
    'studio.title.placeholder':         'Namnge konversationen',
    'studio.title.rename_tip':          'Byt namn - konv {id}',
    'studio.title.renamed':             'Konversationen bytte namn',
    'studio.title.reverted':            'Titeln återställd till automatisk',
    'studio.title.rename_error':        'Kunde inte byta namn: {error}',

    // Avsändare.
    'studio.sender.you':                'Du',
    'studio.sender.user':               'Användare',
    'studio.sender.assistant':          'Assistent',

    // Datumavdelare mellan meddelandegrupper (dygnsbrytningar i tråden).
    'studio.day.today':                 'Idag',
    'studio.day.yesterday':             'Igår',

    // Kolumnknappar (chatt / filer / redigerare / förhandsvisning).
    'studio.pane.chat':                 'Chatt',
    'studio.pane.document':             'Dokument',
    'studio.pane.files':                'Filer',
    'studio.pane.editor':               'Redigerare',
    'studio.pane.preview':              'Förhandsvisning',
    'studio.pane.toggle_tip':           'Växla {label}',
    'studio.pane.toggle_aria':          'Växla kolumnen {label}',
    'studio.pane.switch_aria':          'Visa {label}',
    'studio.mobile.hint':               'Studio fungerar bäst på en större skärm. Byt panel med flikarna ovan.',
    'studio.mobile.hint_dismiss':       'Stäng',

    // Tomt läge för en ny tråd: panelerna finns bara när innehåll finns, så en
    // helt ny konversation berättar vad som KOMMER att dyka upp och var. Raderna
    // följer trådtypen: snabbtrådar (ingen VM) får bara dokumentraden.
    'studio.empty.heading':             'Det agenten skapar öppnas bredvid chatten.',
    'studio.empty.app':                 'En app du ber om dyker upp som en live-förhandsvisning.',
    'studio.empty.doc':                 'Ett dokument öppnas i redigeraren.',
    'studio.empty.files':               'Arbetsfiler hamnar i panelen Filer.',

    // Dokumentkolumn: flikraden (öppna flikar + nedtonade "spök"-flikar för
    // stängda dokument) + dokumentkortet i chatten.
    'studio.doc.close_tab':             'Stäng {name}',
    'studio.doc.reopen':                'Öppna {name} igen',
    'studio.doc.changes':               'Förslag på ändringar ({count})',
    'studio.doc.download_tip':          'Ladda ner .docx',
    'studio.doc.download_pdf_tip':      'Ladda ner som PDF',
    'studio.doc.load_failed':           'Kunde inte ladda konversationens dokument.',
    'studio.doc.card_opened':           'Dokument öppnat',
    'studio.doc.card_open_tip':         'Öppna {name}',

    // Komponering.
    'studio.composer.ask_name':         'Fråga {name}…',
    'studio.composer.message_agent':    'Skriv till agenten…',
    'studio.composer.attach_tip':       'Bifoga filer - upp till {max}, {cap} MB styck',
    'studio.composer.send':             'Skicka',
    'studio.composer.stop':             'Stoppa',
    'studio.composer.stop_tip':         'Stoppa agenten',
    'studio.composer.remove_ref':       'Ta bort referens',
    'studio.composer.remove_ref_aria':  'Ta bort referensen {name}',
    'studio.composer.remove_file':      'Ta bort {name}',

    // Gör om en snabbtråd till en full agenttråd.
    'studio.convert.title':             'Gör om till en agenttråd?',
    'studio.convert.body':              'Tråden får en egen sandbox så att agenten kan köra uppgifter, redigera filer och bygga appar. Konversationen hittills följer med.',
    'studio.convert.confirm':           'Gör om',
    'studio.convert.done':              'Omgjord till en agenttråd',
    'studio.convert.tip':               'Ge tråden en sandbox så att agenten kan köra uppgifter',
    'studio.convert.button':            'Gör om till agent',

    // Ta bort app (yta).
    'studio.del_app.title':             'Ta bort den här appen?',
    'studio.del_app.body':              'Detta förstör appens sandbox och dess beständiga disk. Konversationen finns kvar. Detta kan inte ångras.',
    'studio.del_app.confirm':           'Ta bort app',
    'studio.del_app.tip':               'Ta bort appen {slug}',
    'studio.del_app.button':            'Ta bort app',

    // Ta bort hela konversationen.
    'studio.del_conv.title':            'Ta bort konversation?',
    'studio.del_conv.body':             'Detta tar permanent bort konversationen och dess meddelanden. Detta kan inte ångras.',
    'studio.del_conv.body_app':         'Detta tar permanent bort konversationen och dess meddelanden, och förstör dess app "{slug}". Detta kan inte ångras.',
    'studio.del_conv.confirm_word':     'ta bort',
    'studio.del_conv.confirm_label':    'Ta bort konversation',
    'studio.del_conv.also_files_one':   'Ta även bort den här konversationens {count} levererade fil (annars behålls den, fortfarande nedladdningsbar)',
    'studio.del_conv.also_files_other': 'Ta även bort den här konversationens {count} levererade filer (annars behålls de, fortfarande nedladdningsbara)',
    'studio.del_conv.done':             'Konversationen togs bort',
    'studio.del_conv.tip':              'Ta bort konversation',
    'studio.del_conv.aria':             'Ta bort konversation',

    // Delningsmodal (endast nyckeln som inte redan finns i basen).
    'studio.share.mode_aria':           'Delningsläge',

    // Släpp-för-att-bifoga-overlay (chattkolumnen).
    'studio.drop.title':                'Släpp för att bifoga',
    'studio.drop.hint':                 'Upp till {max} filer, {cap} MB styck',

    // Bekräfta att osparade ändringar slängs.
    'studio.discard.title':             'Släng osparade ändringar?',
    'studio.discard.body':              'Du har osparade ändringar i den öppna filen. Att lämna slänger dem.',
    'studio.discard.confirm':           'Släng',

    // Filpanel (det aktiva /work-trädet + konversationens filer).
    'studio.files.title':               'Filer',
    'studio.files.upload_tip':          'Ladda upp till den här mappen',
    'studio.files.new_file_tip':        'Ny fil',
    'studio.files.new_folder_tip':      'Ny mapp',
    'studio.files.refresh_tip':         'Uppdatera',
    'studio.files.zip_tip':             'Ladda ner arbetsytan som en zip',
    'studio.files.zip_aria':            'Ladda ner arbetsytan',
    'studio.files.offline_paused':      'Appen är pausad. Återuppta den för att bläddra och redigera dess filer.',
    'studio.files.offline_asleep':      'Arbetsytan sover. Den vaknar vid nästa meddelande.',
    'studio.files.resume':              'Återuppta',
    'studio.files.check_again':         'Kontrollera igen',
    'studio.files.empty_folder':        'Tom mapp.',
    'studio.files.dl_all_tip':          'Ladda ner alla levererade filer som en zip',
    'studio.files.dl_all_aria':         'Ladda ner alla filer',
    'studio.files.published':           'Publicerad',
    'studio.files.published_tip':       'Levererad av agenten',
    'studio.files.download':            'Ladda ner',
    'studio.files.download_aria':       'Ladda ner {name}',
    'studio.files.drop_title':          'Släpp för att ladda upp',
    'studio.files.drop_hint':           'Filer landar i {path}',
    'studio.send_to_slack.action':      'Skicka till Slack',
    'studio.send_to_slack.title':       'Skicka till Slack',
    'studio.send_to_slack.subtitle':    'Publicera {name} i en Slack-kanal.',
    'studio.send_to_slack.channel_label': 'Kanal',
    'studio.send_to_slack.channel_ph':  'Välj en kanal',
    'studio.send_to_slack.loading':     'Laddar kanaler...',
    'studio.send_to_slack.not_connected': 'Den här arbetsytan har ingen Slack-anslutning.',
    'studio.send_to_slack.no_channels': 'HQ är inte med i någon kanal än. Lägg till HQ-appen i en kanal först.',
    'studio.send_to_slack.send':        'Skicka',
    'studio.send_to_slack.sending':     'Skickar...',
    'studio.send_to_slack.cancel':      'Avbryt',
    'studio.send_to_slack.sent':        'Skickat till {channel}',
    'studio.send_to_slack.failed':      'Kunde inte skicka: {error}',

    // Filsystem-frågor + validering.
    'studio.fs.name_required_file':     'Ange ett filnamn.',
    'studio.fs.name_required_folder':   'Ange ett mappnamn.',
    'studio.fs.name_no_slashes':        'Namnet får inte innehålla snedstreck.',
    'studio.fs.name_real':              'Välj ett riktigt namn.',
    'studio.fs.new_file_title':         'Ny fil',
    'studio.fs.new_folder_title':       'Ny mapp',
    'studio.fs.name_label':             'Namn',
    'studio.fs.create':                 'Skapa',
    'studio.fs.created_in':             'Skapas i {path}',
    'studio.fs.delete_title':           'Ta bort {name}?',
    'studio.fs.delete_dir_body':        'Detta tar bort mappen och allt i den. Detta kan inte ångras.',
    'studio.fs.delete_file_body':       'Detta tar bort filen. Detta kan inte ångras.',
    'studio.fs.drop_asleep':            'Arbetsytan sover, så filer kan inte sparas än. Skicka ett meddelande för att väcka den och försök sedan igen.',
    'studio.fs.zip_error':              'Kunde inte bygga zip-filen för arbetsytan.',
    'studio.fs.download_failed':        'Nedladdningen misslyckades.',

    // Redigerarens verktygsfält + tomt läge.
    'studio.editor.unsaved':            'Osparade ändringar',
    'studio.editor.preview':            'Förhandsvisa',
    'studio.editor.view_source':        'Visa källa',
    'studio.editor.referenced':         'Refererad - klicka för att ta bort',
    'studio.editor.reference':          'Peka agenten på den här filen',
    'studio.editor.reference_aria':     'Referera {name} i komponeringsrutan',
    'studio.editor.close':              'Stäng',
    'studio.editor.empty':              'Öppna en fil för att visa eller redigera den här.',

    // Förhandsvisningspanel (aktiv iframe + tillståndskort).
    'studio.preview.live':              'Aktiv förhandsvisning',
    'studio.preview.meta_tip':          '{slug} - {mode}, port {port}',
    'studio.preview.frame_tip':         'Aktiv förhandsvisning av {slug}',
    'studio.preview.inspect_start':     'Välj ett element att peka agenten på',
    'studio.preview.inspect_cancel':    'Avbryt elementväljaren',
    'studio.preview.open_tab':          'Öppna i en ny flik',
    'studio.preview.empty':             'Ingen förhandsvisning än. Be agenten bygga en app så visas den här.',
    'studio.preview.title_resuming':    'Återupptar appen',
    'studio.preview.title_paused':      'Appen är pausad',
    'studio.preview.title_booting':     'Startar appen',
    'studio.preview.title_archived':    'Appen är arkiverad',
    'studio.preview.title_failed':      'Appen kunde inte starta',
    'studio.preview.title_destroyed':   'Appen togs bort',
    'studio.preview.title_other':       'Apptillstånd: {state}',
    'studio.preview.body_resuming':     'Återställer sandboxen från dess senaste ögonblicksbild. Det tar några sekunder.',
    'studio.preview.body_paused':       'Den lades i viloläge för att spara resurser. Återuppta den för att fortsätta där den slutade.',
    'studio.preview.body_booting':      'Startar sandboxen. Det tar några sekunder.',
    'studio.preview.body_archived':     'Dess sandbox arkiverades. Be agenten bygga om den för att få tillbaka den.',
    'studio.preview.body_failed':       'Något gick fel när den skulle starta. Be agenten försöka igen.',
    'studio.preview.body_destroyed':    'Dess sandbox och disk togs bort. Be agenten bygga om den.',
    'studio.preview.resuming':          'Återupptar…',
    'studio.preview.resume_app':        'Återuppta app',

    // Körtidsfel i förhandsvisningen (postMessage från iframen).
    'studio.preview_err.runtime':       'Körtidsfel',
    'studio.preview_err.rejection':     'Ohanterat avslag: {reason}',
    'studio.preview_err.build':         'Byggfel ({source})',
    'studio.preview_err.dismiss_all':   'Avfärda alla {count}',
    'studio.preview_err.fix':           'Försök åtgärda',
    'studio.preview_err.dismiss':       'Avfärda',

    // Skrivskyddade banderoller + uppmaningar.
    'studio.readonly.schedule':         'Detta är en notering om en schemalagd körning med {name}, inte en aktiv chatt.',
    'studio.readonly.opening':          'Öppnar…',
    'studio.readonly.start_with':       'Starta en ny chatt med {name}',
    'studio.readonly.channel':          'Den här konversationen lever i kanalen - svara där för att svara.',
    'studio.readonly.admin':            'Visas som administratör - skrivskyddad.',

    // Administratörsvy-banderoll + erbjudande till icke-deltagare.
    'studio.admin.banner_label':        'Administratörsvy.',
    'studio.admin.banner_text':         'Du tittar på en konversation du inte deltar i. Varje inläsning granskas.',
    'studio.admin.leave_tip':           'Lämna administratörsvyn',
    'studio.admin.exit':                'Avsluta',
    'studio.admin.offer_title':         'Du deltar inte i den här konversationen',
    'studio.admin.offer_detail':        'Som arbetsyteadministratör kan du öppna den i administratörsvy. Det granskas.',
    'studio.admin.offer_btn':           'Öppna i administratörsvy',

    // Fel (banderoller, notiser, historikinläsningar, sändningar).
    'studio.err.unknown':               'okänt fel',
    'studio.err.no_response':           'Inget svar från agenten. Försök skicka igen.',
    'studio.err.file_too_large':        'Den filen är för stor för att öppna här.',
    'studio.err.resume':                'Kunde inte återuppta appen (HTTP {status}): {detail}',
    'studio.err.share_create':          'Kunde inte skapa en delningslänk (HTTP {status}): {detail}',
    'studio.err.share_public':          'Kunde inte göra appen offentlig (HTTP {status}): {detail}',
    'studio.err.share_private':         'Kunde inte växla tillbaka till inbjudan-bara (HTTP {status}): {detail}',
    'studio.err.history_load':          'Kunde inte läsa in historiken (HTTP {status}).',
    'studio.err.history_load_failed':   'Kunde inte läsa in historiken: {error}',
    'studio.err.send':                  'Kunde inte skicka meddelandet (HTTP {status}).',
    'studio.err.send_failed':           'Kunde inte skicka meddelandet: {error}',
    'studio.err.stop':                  'Kunde inte stoppa agenten: {error}',

    // Korta meningar vid turfel (rått rust-fel mappat till begriplig text).
    'studio.turn_err.unreachable':      'Agenten kunde inte slutföra - dess arbetsyta var inte nåbar. Skicka igen för att försöka på nytt.',
    'studio.turn_err.timeout':          'Agenten hann inte slutföra i tid. Skicka igen för att försöka på nytt.',
    'studio.turn_err.rate_limit':       'Agenten är hastighetsbegränsad just nu. Vänta en stund och skicka igen.',
    'studio.turn_err.generic':          'Agenten kunde inte slutföra den turen. Skicka igen för att försöka på nytt.',

    // Uppladdningsgränser + fel.
    'studio.upload.too_big':            '{name} är {size} MB - över gränsen på {cap} MB.',
    'studio.upload.too_many':           'Du kan bifoga upp till {max} filer åt gången.',
    'studio.upload.failed':             'Kunde inte ladda upp {name}: {error}',

    // ── web-ui MeView.vue (me.*) ──
    'me.header.title':                  'Du',
    'me.header.subtitle':               'Vad dina agenter vet om dig, och hur de minns det.',

    // Klock- och tidszonsinställningar.
    'me.clock.auto':                    'Automatiskt',
    'me.clock.12h':                     '12-timmars',
    'me.clock.24h':                     '24-timmars',
    'me.tz.device':                     'Den här enheten',
    'me.tz.profile':                    'Min profil',

    // Identitetsblock ("Du").
    'me.identity.heading':              'Du',
    'me.avatar.change':                 'Byt avatar',
    'me.avatar.uploading':              'Laddar upp…',
    'me.avatar.hint':                   'PNG, JPG, SVG, WebP eller GIF, upp till 2 MB.',
    'me.avatar.synced':                 'Din avatar synkas från din anslutna Slack/Teams-profil.',
    'me.avatar.updated':                'Avatar uppdaterad.',
    'me.avatar.err_type':               'Bildtypen stöds inte. Använd PNG, JPG, SVG, WebP eller GIF.',
    'me.avatar.err_size':               'Bilden är över 2 MB. Förminska den och försök igen.',
    'me.avatar.err_upload':             'Kunde inte ladda upp bilden. Försök igen.',
    'me.identity.name':                 'Namn',
    'me.identity.name_aria':            'Ditt namn',
    'me.identity.name_ph':              'Ditt namn',
    'me.identity.email':                'E-post',
    'me.identity.title':                'Titel',
    'me.identity.connected_as':         'Ansluten som',
    'me.identity.locale':               'Språk',
    'me.identity.locale_ph':            'Välj ett språk',
    'me.identity.timezone':             'Tidszon',
    'me.identity.tz_ph':                'Välj din tidszon',
    'me.identity.tz_aria':              'Din tidszon',
    'me.identity.tz_segment_aria':      'Tidszon för visade tider',
    'me.identity.tz_showing':           'Visar tider i {tz}.',
    'me.identity.tz_device_switch':     'Den här enhetens zon. Växla för att visa tider i {tz}.',
    'me.identity.tz_none':              'Ingen tidszon på din arbetsyteprofil, så tider använder den här enhetens zon.',
    'me.identity.role':                 'Roll',
    'me.identity.role_admin':           'Arbetsyteadministratör',
    'me.identity.role_member':          'Medlem',
    'me.identity.schedules':            'Scheman',
    'me.identity.schedules_owned':      '{count} ägda',
    'me.identity.clock':                'Klocka',
    'me.identity.clock_aria':           'Klockformat',
    'me.identity.clock_hint':           'Tider i hela appen, t.ex. {sample}. Automatiskt följer din webbläsare.',

    // Personablock.
    'me.persona.heading':               'Persona',
    'me.persona.distilled':             'Destillerad {when} från {count} meddelanden',
    'me.persona.topics':                'Återkommande ämnen',
    'me.persona.themes':                'Återkommande teman',
    'me.persona.style':                 'Stil',
    'me.persona.style_tone':            'Ton',
    'me.persona.style_length':          'Längd',
    'me.persona.style_emoji':           'Emoji',
    'me.persona.style_punctuation':     'Skiljetecken',
    'me.persona.languages':             'Språk',
    'me.persona.lang_primary':          'primärt',
    'me.persona.active_hours':          'Aktiva timmar',
    'me.persona.top_channels':          'Toppkanaler',
    'me.persona.top_collaborators':     'Toppsamarbeten',
    'me.persona.empty_before':          'Ingen persona än. HQ bygger en profil utifrån dina tidigare meddelanden och uppdaterar den varje vecka. Om du bara har varit på arbetsytan en kort tid, eller om din tenant inte har valt in användarspecifik destillation (samtycke:',
    'me.persona.empty_after':           '), förblir det här tomt.',

    // Minnesfaktablock.
    'me.facts.heading':                 'Vad agenter har skrivit',
    'me.facts.count_one':               '{count} faktum',
    'me.facts.count_other':             '{count} fakta',
    'me.facts.filter_all':              'Alla',
    'me.facts.show_more':               'Visa fler',
    'me.facts.empty':                   'Inga fakta sparade. Agenter skriver fakta här när de lär sig något stabilt om dig - preferenser, återkommande sammanhang, hur du gillar saker. De inkluderar aldrig konversationsinnehåll, bara destillerade, personspecifika påståenden.',
    'me.facts.empty_filtered':          'Inga fakta av typen {type}. Agenter skriver fakta här när de lär sig något stabilt om dig - preferenser, återkommande sammanhang, hur du gillar saker. De inkluderar aldrig konversationsinnehåll, bara destillerade, personspecifika påståenden.',

    // En enskild faktarad (inline-redigering + åtgärder).
    'me.fact.will_replace':             'ersätter, tar inte bort',
    'me.fact.save_aria':                'Spara korrigering',
    'me.fact.cancel_aria':              'Avbryt redigering',
    'me.fact.edit_aria':                'Redigera det här faktumet',
    'me.fact.forget_aria':              'Glöm det här faktumet',
    'me.fact.forget':                   'Glöm',
    'me.fact.length_warn':              'Ett faktum måste vara mellan 1 och 4096 tecken.',

    // Block för minnesfrysning per agent.
    'me.freeze.heading':                'Agenter som kan läsa ditt minne',
    'me.freeze.lede':                   'Varje agent kan normalt läsa varje faktum andra agenter har skrivit om dig, så att de inte ställer samma frågor två gånger. Frys en agent för att stänga av den från ditt minne - den slutar se dina fakta vid sin nästa konversation. Nya fakta som skrivs av andra agenter landar fortfarande i ditt minne; du bestämmer bara vem som får läsa dem.',
    'me.freeze.frozen_at':              'Frusen {when}',
    'me.freeze.can_read':               'Kan läsa ditt minne',
    'me.freeze.freeze':                 'Frys',
    'me.freeze.unfreeze':               'Tina',
    'me.freeze.empty':                  'Inga agenter aktiverade i den här arbetsytan än.',

    // Block för rätten att bli raderad.
    'me.erase.heading':                 'Radera mina data',
    'me.erase.body_before':             'GDPR artikel 17 - begär permanent radering av dina personuppgifter i den här arbetsytan. Rensar dina DM-meddelandetexter, varje faktum agenter har noterat om dig, korpusraderna du författat, din personaprofil och alla privata OAuth-uppgifter du installerat. Ditt arbetsytekonto blir kvar som en gravsten så att andra personers historik förblir intakt.',
    'me.erase.body_warn':               'Detta kan inte ångras.',
    'me.erase.button':                  'Radera mina data',
    'me.erase.erasing':                 'Raderar…',
    'me.erase.confirm_title':           'Radera dina data?',
    'me.erase.confirm_body':            'Detta tar permanent bort dina personuppgifter i den här arbetsytan - meddelanden, fakta, korpusrader, persona och privata uppgifter. Detta kan inte ångras.',
    'me.erase.confirm_word':            'radera',
    'me.erase.confirm_label':           'Radera mina data',
    'me.erase.done':                    'Raderade {msgs} meddelanden, {facts} fakta, {rows} korpusrader och {creds} uppgifter.',

    // Fel-notiser vid spara/åtgärd.
    'me.err.save_clock':                'Kunde inte spara din klockinställning.',
    'me.err.save_timezone':             'Kunde inte spara din tidszon.',
    'me.err.save_name':                 'Kunde inte spara ditt namn.',
    'me.err.save_locale':               'Kunde inte spara ditt språk.',
    'me.err.erase_failed':              'Raderingen misslyckades. Inget togs bort.',
    'me.err.save_failed':               'Kunde inte spara din korrigering.',
    'me.err.forget_failed':             'Kunde inte glömma det faktumet.',
    'me.err.action_failed':             'Något gick fel.',

    // Lyckade notiser vid spara/åtgärd.
    'me.toast.name_updated':            'Namnet uppdaterat',
    'me.toast.timezone_updated':        'Tidszonen uppdaterad',
    'me.toast.locale_updated':          'Språket uppdaterat',

    // Bekräfta att glömma faktum.
    'me.forget.confirm_title':          'Glöm det här faktumet?',
    'me.forget.confirm_body':           'HQ slutar använda "{fact}". Detta kan inte ångras.',
    'me.forget.confirm_label':          'Glöm',
  },
};
